import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import SharedButton, { IButton } from '../button';
import { Undo } from '../../assets/icons';
import theme from '../../themes';
import { WithCreditsKeypadInput } from '../../betting/bet-with-credits';
import { Heading, robotoFonts } from '../typography';

interface IDigit {
  value: string;
  additionalProps?: any;
}

const basicDigits: IDigit[] = [
  { value: '7' },
  { value: '8' },
  { value: '9' },
  { value: '4' },
  { value: '5' },
  { value: '6' },
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '0', additionalProps: { doubleWidth: true } },
  { value: 'DEL' }
];

const defaultDigits: IDigit[] = [
  { value: '7' },
  { value: '8' },
  { value: '9' },
  { value: '4' },
  { value: '5' },
  { value: '6' },
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '.' },
  { value: '0' },
  { value: 'DEL' }
];

export const betslipDigits: IDigit[] = [
  { value: '7' },
  { value: '8' },
  { value: '9' },
  { value: '+10' },
  { value: '4' },
  { value: '5' },
  { value: '6' },
  { value: '+25' },
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '+50' },
  { value: '00' },
  { value: '0' },
  { value: '.' },
  { value: '+100' }
];

export const betslipPayoutDigits: IDigit[] = [
  { value: '7' },
  { value: '8' },
  { value: '9' },
  { value: '+10' },
  { value: '4' },
  { value: '5' },
  { value: '6' },
  { value: '+25' },
  { value: '1' },
  { value: '2' },
  { value: '3' },
  { value: '+50' },
  { value: '00' },
  { value: '0' },
  { value: '+100' }
];

interface IUpdateKeypadValueProps {
  existingValue?: string;
  callback(newValue: string): void;
  maxValue?: number | undefined;
  isBetslipKeyPad?: boolean;
  onDeleteEmpty?(): void;
}

export const parseKeypadValue = (
  key: string,
  existingValue: string = '',
  maxValue: any,
  isBetslipKeyPad?: boolean,
  onDeleteEmpty?: () => void
) => {
  const alreadyContainsOneDecimalPoint = key === '.' && existingValue.indexOf('.') !== -1;
  const firstCharIsPlus = key.substring(0, 1) === '+';
  const decimalPlaces = existingValue ? existingValue.slice(existingValue.indexOf('.'), existingValue.length) : '';
  const alreadyHas2DecimalPlaces =
    key !== 'DEL' && !firstCharIsPlus && (decimalPlaces.length > 2 || decimalPlaces.length + key.length > 3);
  let newStake = existingValue;

  if (alreadyContainsOneDecimalPoint || alreadyHas2DecimalPlaces) {
    return newStake;
  }

  if (key === 'DEL') {
    if (existingValue.length === 0) {
      if (onDeleteEmpty) {
        onDeleteEmpty();
      }
      return '';
    }
    return existingValue.slice(0, existingValue.length - 1);
  }

  if (firstCharIsPlus) {
    const value = key.substring(1, key.length);
    if (existingValue === '' || existingValue === undefined) {
      return value;
    } else {
      const updatedStake = parseFloat(existingValue) + parseInt(value, 10);
      if (!Number.isInteger(updatedStake)) {
        newStake = updatedStake.toFixed(2);
      } else {
        newStake = updatedStake.toString();
      }
    }
  } else {
    if (isBetslipKeyPad && existingValue.startsWith('0')) {
      newStake = key;
    } else newStake = existingValue + key;
  }

  const parsedNewStake = parseFloat(newStake);
  if (maxValue && (parsedNewStake > maxValue || parsedNewStake === 0)) {
    return existingValue;
  }
  return newStake;
};

export const setupOnKeyPress =
  ({ maxValue, existingValue = '', callback, isBetslipKeyPad, onDeleteEmpty }: IUpdateKeypadValueProps) =>
  (key: string) =>
    callback(parseKeypadValue(key, existingValue, maxValue, isBetslipKeyPad, onDeleteEmpty));

type KeypadButton = {
  label: string;
  callback: any;
  type: IButton['modifiers'];
};

type KeypadProps = {
  className?: string;
  existingValue?: string;
  showInputPreview?: boolean;
  inputPreviewValue?: string;
  digits?: IDigit[];
  buttons?: KeypadButton[];
  maxValue?: number;
  margin?: string;
  toggleKeypad?(): void;
  onKeyPress(newValue: string): void;
  isBetslipKeyPad?: boolean;
  onDeleteEmpty?(): void;
  withCredits?: boolean;
  hideKeyboardDot?: boolean;
};

export function PopupKeypad({
  maxValue,
  existingValue,
  margin,
  digits,
  className,
  buttons,
  inputPreviewValue,
  showInputPreview,
  isBetslipKeyPad,
  toggleKeypad,
  onDeleteEmpty,
  onKeyPress,
  withCredits = false
}: KeypadProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, { capture: true });
    return () => document.removeEventListener('mousedown', handleClickOutside, { capture: true });
  }, []);

  const handleKeyPress = setupOnKeyPress({
    maxValue,
    existingValue,
    callback: onKeyPress,
    isBetslipKeyPad,
    onDeleteEmpty
  });

  const handleClickOutside = (event: MouseEvent) =>
    !ref.current?.contains(event.target as HTMLElement) && toggleKeypad?.();

  const handleDeletePress = () => handleKeyPress('DEL');

  return (
    <Wrapper
      data-testid="qa-keypad-wrapper"
      onMouseDown={e => e.preventDefault()}
      ref={ref}
      className={`qa-keypad-wrapper ${className || ''}`}>
      {showWithCreditsComponent({ showInputPreview, withCredits, inputPreviewValue, handleDeletePress }) ||
        showInputPreviewComponent({ showInputPreview, inputPreviewValue, handleDeletePress })}
      {(digits || defaultDigits).map((digit: IDigit) => {
        const keyPress = () => handleKeyPress(digit.value);
        return (
          <Button
            {...digit.additionalProps}
            throttleFor={0}
            className="qa-keypad-input"
            type="button"
            key={digit.value}
            onClick={keyPress}>
            {digit.value === 'DEL' ? <Undo fill={theme.colors.brand.foreground} /> : digit.value}
          </Button>
        );
      })}
      <span className="qa-click-inside" />
      {buttons && <ButtonsHolder margin={margin}>{renderKeypadButtons(buttons)}</ButtonsHolder>}
    </Wrapper>
  );
}

const renderKeypadButtons = (keypadButtons: KeypadButton[] = []) => {
  return keypadButtons.map((button: KeypadButton) => {
    return (
      <SharedButton
        buttonSize="stretch"
        modifiers={button.type}
        key={button.label}
        className="qa-action-button"
        onClick={button.callback}>
        <span>{button.label}</span>
      </SharedButton>
    );
  });
};

function showWithCreditsComponent({
  showInputPreview,
  withCredits,
  inputPreviewValue,
  handleDeletePress
}: {
  showInputPreview?: boolean;
  withCredits: boolean;
  inputPreviewValue?: string;
  handleDeletePress(): void;
}) {
  return (
    showInputPreview &&
    withCredits && <WithCreditsKeypadInput inputPreviewValue={inputPreviewValue} onDeletePress={handleDeletePress} />
  );
}

const formatStakeAmountWithDollarSign = (amount: string) => {
  return `$${amount}`;
};

function showInputPreviewComponent({
  showInputPreview,
  inputPreviewValue,
  handleDeletePress
}: {
  showInputPreview?: boolean;
  inputPreviewValue?: string;
  handleDeletePress(): void;
}) {
  return (
    showInputPreview && (
      <InputPreviewHolder>
        <InputPreview as="div" size="2xl" weight="bold">
          {formatStakeAmountWithDollarSign(inputPreviewValue || '0')}
        </InputPreview>
        <DeleteIconHolder onClick={handleDeletePress} data-testid="qa-keypad-delete">
          <Undo fill={theme.colors.neutral} />
        </DeleteIconHolder>
      </InputPreviewHolder>
    )
  );
}

export const BasicKeypad = (props: Omit<KeypadProps, 'digits'>) => <PopupKeypad {...props} digits={basicDigits} />;
export const CashKeypad = (props: KeypadProps) => (
  <CashKeypadWrapper>
    <PopupKeypad {...props} digits={defaultDigits} />
  </CashKeypadWrapper>
);

export const BetslipKeypad = (props: KeypadProps & { open: boolean; title?: string; hasError?: boolean }) => {
  const { open, title, hasError, ...restProps } = props;
  const className = `qa-betslip-keypad qa-betslip-keypad-${open ? 'open' : 'closed'}`;
  return (
    <BetslipKeypadBackground open={open} className={className} data-testid="qa-betslip-keypad">
      <BetslipKeypadWrapper hasError={hasError}>
        {title && <BetslipKeypadTitle hasError={hasError}>{title}</BetslipKeypadTitle>}
        <PopupKeypad {...restProps} digits={betslipDigits} />
      </BetslipKeypadWrapper>
    </BetslipKeypadBackground>
  );
};

const Wrapper = styled.div<{ className: string }>`
  justify-content: center;
  display: inline-flex;
  flex-wrap: wrap;
  background-color: ${({ theme }) => theme.colors.white};
  max-width: 100%;
`;

const CashKeypadWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0.8em;
  box-shadow: ${({ theme }) => theme.shadows.heavy};
  padding: 1rem 1rem 1.5rem;
  width: 28.4rem;
`;

const InputPreviewHolder = styled.div`
  display: flex;
  width: 100%;
  height: 84px;
  margin-bottom: 1rem;
  text-align: right;
  align-items: center;
  justify-content: center;
`;

const InputPreview = styled(Heading)`
  width: 100%;
  border-bottom: solid 2px ${({ theme }) => theme.components.stake.borderColor};
  color: ${({ theme }) => theme.components.stake.fontColor};
  overflow-x: auto;
  margin-left: 1.6rem;
`;

const DeleteIconHolder = styled.div`
  padding: 2rem 1.6rem 2rem 2rem;
`;

const Button = styled(SharedButton)<{ doubleWidth?: boolean; isHidden?: boolean }>`
  ${({ isHidden }) => isHidden && `visibility: hidden;`}
  font-weight: 500;
  background-color: ${({ theme }) => theme.colors.white};
  margin: 4px;
  border: 1px solid ${({ theme }) => theme.colors.supporting.accent100};
  font-size: ${robotoFonts['2xl']};
  height: 7.4rem;
  width: 7.4rem;
  border-radius: 10rem;
  transition: all 0.1s ease-in;
  position: relative;

  &:active {
    animation: keypress_animation 1s ease-in-out;
  }

  @keyframes keypress_animation {
    0% {
      background-color: ${({ theme }) => theme.colors.white};
    }
    10% {
      background-color: ${({ theme }) => theme.colors.toggle.disabledBackgroundColour};
    }
    100% {
      background-color: ${({ theme }) => theme.colors.white};
    }
  }

  ${({ doubleWidth }) =>
    doubleWidth &&
    css`
      width: 14.8rem;
    `};
`;

const ButtonsHolder = styled.div<{ margin?: string }>`
  ${({ margin }) => `
    margin: ${margin};
  `};
  justify-content: space-around;
  align-items: center;
  display: flex;
  width: 100%;
`;

const BetslipKeypadBackground = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  position: fixed;
  transition: right linear 100ms;
  width: 35.5rem;
  ${({ open, theme }) => css`
    top: ${theme.generic.headerHeight};
    z-index: ${theme.layers.betslip};
    right: ${open ? '45rem' : '-60rem;'};
  `};
`;

const BetslipKeypadWrapper = styled.div<{ hasError?: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0.4em;
  display: flex;
  padding: 1rem 0;
  margin: 0.8rem;
  position: relative;
  box-shadow: ${theme.shadows.drop};

  ${({ theme, hasError }) =>
    hasError &&
    `
  ${InputPreview} {
    color: ${theme.colors.status.error};
    border-bottom: solid 2px ${theme.colors.status.error};
  }
  `}
`;

const BetslipKeypadTitle = styled.span<{ hasError?: boolean }>`
  position: absolute;
  left: 1.4rem;
  top: 1rem;
  color: ${({ theme, hasError }) => (hasError ? theme.colors.status.error : theme.components.stake.fontColor)};
`;
